import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Select.module.css';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
};

export type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

type ChangeLike = { target: { value: string; name?: string } };

export type SelectProps = {
  label: string;
  /** Inline контексты (таблицы) — визуально скрыть label, оставить как sr-only. */
  hideLabel?: boolean;
  /** Явный aria-label когда `hideLabel` не дублирует нужный текст (U4.7). */
  'aria-label'?: string;
  options?: SelectOption[];
  grouped?: SelectOptionGroup[];
  value?: string | undefined;
  defaultValue?: string;
  onChange?: (event: ChangeLike) => void;
  placeholder?: string;
  hint?: string;
  error?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  /** F96 — первое поле диалога: `Dialog` ставит на него фокус при открытии (F61). */
  autoFocus?: boolean;
  id?: string;
  name?: string;
  className?: string;
  renderOption?: (opt: SelectOption, state: { selected: boolean; active: boolean }) => ReactNode;
  renderValue?: (opt: SelectOption | undefined) => ReactNode;
  size?: 'sm' | 'md';
};

type SelectHandle = {
  focus: () => void;
};

function flattenOptions(
  options: SelectOption[] | undefined,
  grouped: SelectOptionGroup[] | undefined,
): SelectOption[] {
  if (grouped && grouped.length > 0) return grouped.flatMap((g) => g.options);
  return options ?? [];
}

export const Select = forwardRef<SelectHandle, SelectProps>(function Select(
  {
    label,
    hideLabel = false,
    options,
    grouped,
    value,
    defaultValue,
    onChange,
    placeholder = '—',
    hint,
    error,
    disabled,
    required,
    autoFocus,
    id,
    name,
    className,
    renderOption,
    renderValue,
    size = 'md',
  },
  ref,
) {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const listboxId = `${triggerId}-listbox`;
  const hintId = hint ? `${triggerId}-hint` : undefined;
  const errorId = error ? `${triggerId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const flat = useMemo(() => flattenOptions(options, grouped), [options, grouped]);
  const isControlled = value !== undefined;
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue ?? '');
  const current = isControlled ? (value ?? '') : uncontrolled;
  const selectedOption = flat.find((o) => o.value === current);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  // Portal target — ближайший `<dialog>` (если Select внутри модалки). Native dialog
  // живёт в Top Layer браузера — portal в `document.body` окажется ПОД ним. Чтобы
  // dropdown был поверх Dialog-backdrop, рендерим его как child того же dialog.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const typeBufferRef = useRef<{ buffer: string; timer: ReturnType<typeof setTimeout> | null }>({
    buffer: '',
    timer: null,
  });

  useImperativeHandle(ref, () => ({ focus: () => triggerRef.current?.focus() }), []);

  const commit = useCallback(
    (nextValue: string) => {
      if (!isControlled) setUncontrolled(nextValue);
      onChange?.({ target: { value: nextValue, name } });
    },
    [isControlled, onChange, name],
  );

  // S-FIX-2-FE.2.3: smart positioning — flip-up если снизу не помещается.
  const positionPanel = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const panelH = panelRef.current?.offsetHeight ?? 280;

    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;

    let top: number;
    if (spaceBelow >= panelH + 8) {
      top = rect.bottom + 4;
    } else if (spaceAbove >= panelH + 8) {
      top = rect.top - panelH - 4;
    } else {
      top = 8;
    }

    // С-I7 Р.5 (гейт, круг 1): список не уже поля, но и не уже своих пунктов — у узкого поля
    // («Состояние» заказов) пункты резались до «за…». Ширина — по содержимому, у края окна — сдвиг.
    const panelW = Math.max(rect.width, panelRef.current?.offsetWidth ?? rect.width);
    let left = rect.left;
    if (left + panelW > viewportW - 8) {
      left = Math.max(8, viewportW - panelW - 8);
    }

    setPanelPos({ top, left, width: rect.width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    positionPanel();
    // Второй замер — когда панель уже в разметке: её ширина по пунктам и высота известны только тогда.
    const raf = requestAnimationFrame(positionPanel);
    // Ищем ближайший открытый `<dialog>`. Если есть — portal target = он,
    // dropdown будет child-ом dialog-а и попадёт в тот же Top Layer.
    const closestDialog = triggerRef.current?.closest('dialog') ?? null;
    setPortalTarget(
      closestDialog instanceof HTMLElement
        ? closestDialog
        : typeof document !== 'undefined'
          ? document.body
          : null,
    );
    const onScroll = () => positionPanel();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const activeRef = optionRefs.current[activeIndex];
    if (activeRef) activeRef.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const openWithFocus = useCallback(() => {
    if (disabled) return;
    const idx = flat.findIndex((o) => o.value === current);
    setActiveIndex(idx >= 0 ? idx : flat.findIndex((o) => !o.disabled));
    setOpen(true);
  }, [disabled, flat, current]);

  const selectAt = useCallback(
    (idx: number) => {
      const opt = flat[idx];
      if (!opt || opt.disabled) return;
      commit(opt.value);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [flat, commit],
  );

  const moveActive = useCallback(
    (dir: 1 | -1) => {
      if (flat.length === 0) return;
      let next = activeIndex;
      for (let i = 0; i < flat.length; i++) {
        next = (next + dir + flat.length) % flat.length;
        if (!flat[next]?.disabled) break;
      }
      setActiveIndex(next);
    },
    [flat, activeIndex],
  );

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openWithFocus();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectAt(activeIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveIndex(flat.findIndex((o) => !o.disabled));
    } else if (e.key === 'End') {
      e.preventDefault();
      for (let i = flat.length - 1; i >= 0; i--) {
        if (!flat[i]?.disabled) {
          setActiveIndex(i);
          break;
        }
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const buf = typeBufferRef.current;
      buf.buffer += e.key.toLowerCase();
      if (buf.timer) clearTimeout(buf.timer);
      buf.timer = setTimeout(() => {
        buf.buffer = '';
      }, 600);
      const startIdx = activeIndex + 1;
      for (let i = 0; i < flat.length; i++) {
        const probe = (startIdx + i) % flat.length;
        const opt = flat[probe];
        if (!opt || opt.disabled) continue;
        if (opt.label.toLowerCase().startsWith(buf.buffer)) {
          setActiveIndex(probe);
          break;
        }
      }
    }
  };

  const displayNode = renderValue
    ? renderValue(selectedOption)
    : (selectedOption?.label ?? <span className={styles.placeholder}>{placeholder}</span>);

  const listContent = grouped ? (
    grouped.map((group) => (
      <div key={group.label} role="group" aria-label={group.label} className={styles.group}>
        <div className={styles.groupLabel}>{group.label}</div>
        <div className={styles.list} role="presentation">
          {group.options.map((opt) => {
            const globalIdx = flat.indexOf(opt);
            return renderOptionItem(opt, globalIdx);
          })}
        </div>
      </div>
    ))
  ) : (
    <div
      id={listboxId}
      className={styles.list}
      role="listbox"
      aria-labelledby={triggerId}
      tabIndex={-1}
    >
      {(options ?? []).map((opt) => {
        const globalIdx = flat.indexOf(opt);
        return renderOptionItem(opt, globalIdx);
      })}
    </div>
  );

  function renderOptionItem(opt: SelectOption, idx: number): ReactNode {
    const selected = opt.value === current;
    const active = idx === activeIndex;
    return (
      <div
        key={opt.value || `__${idx}`}
        ref={(el) => {
          optionRefs.current[idx] = el;
        }}
        role="option"
        aria-selected={selected}
        aria-disabled={opt.disabled || undefined}
        tabIndex={-1}
        className={[
          styles.option,
          selected ? styles.optionSelected : '',
          active ? styles.optionActive : '',
          opt.disabled ? styles.optionDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseEnter={() => !opt.disabled && setActiveIndex(idx)}
        onMouseDown={(e) => {
          e.preventDefault();
          selectAt(idx);
        }}
      >
        {renderOption ? (
          renderOption(opt, { selected, active })
        ) : (
          <>
            <span className={styles.optionLabel}>{opt.label}</span>
            {opt.hint && <span className={styles.optionHint}>{opt.hint}</span>}
            {selected && (
              <span className={styles.check} aria-hidden="true">
                ✓
              </span>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label
        htmlFor={triggerId}
        className={styles.label}
        style={
          hideLabel
            ? {
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
              }
            : undefined
        }
      >
        {label}
        {/* FE-55 Sprint N+: visible required marker.
            Post-design-review: skip if label already ends with `*` (legacy i18n). */}
        {required && !hideLabel && !(typeof label === 'string' && label.trim().endsWith('*')) && (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
      </label>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        data-autofocus={autoFocus ? 'true' : undefined}
        className={[
          styles.trigger,
          size === 'sm' ? styles.triggerSm : '',
          error ? styles.triggerError : '',
          disabled ? styles.triggerDisabled : '',
          open ? styles.triggerOpen : '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-required={required || undefined}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={describedBy}
        onClick={() => (open ? setOpen(false) : openWithFocus())}
        onKeyDown={handleKey}
        {...(name ? { name } : {})}
      >
        <span className={styles.value}>{displayNode}</span>
        <span className={styles.chevron} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d="M3 4.5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && panelPos && portalTarget
        ? createPortal(
            <div
              ref={panelRef}
              className={styles.panel}
              style={{ top: panelPos.top, left: panelPos.left, minWidth: panelPos.width }}
              role="presentation"
            >
              {listContent}
            </div>,
            portalTarget,
          )
        : null}
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
